/* ============================================================================
   FRONTLINE — shared behaviour (cart, drawer, quick-add, modal, sticky bar)
   Loaded with `defer`. Reads config from window.FL (set in layout/theme.liquid).
   Designed to be resilient: every feature guards on element presence.
   ========================================================================== */
(function () {
  "use strict";

  var CFG = window.FL || (window.FL = {});
  CFG.cartType = CFG.cartType || "drawer";
  CFG.currency = CFG.currency || "EUR";
  CFG.locale = CFG.locale || "nl-NL";
  CFG.strings = CFG.strings || {};
  CFG.freeShipThreshold = typeof CFG.freeShipThreshold === "number" ? CFG.freeShipThreshold : 0; // cents
  if (typeof CFG.cartDrawer === "undefined") CFG.cartDrawer = true; // false = let an external slide-cart app handle the UI

  var t = function (k, fallback) { return CFG.strings[k] || fallback || ""; };

  // Decimal (no currency symbol) + clean Dutch "30,-" notation, site-wide.
  var money = new Intl.NumberFormat(CFG.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  function formatMoney(cents) { return money.format((cents || 0) / 100).replace(/,00$/, ",-"); }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* -------------------------------------------------- toast ------------- */
  var toastEl;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "fl-toast";
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>' +
      "<span></span>";
    $("span", toastEl).textContent = msg;
    requestAnimationFrame(function () { toastEl.classList.add("is-visible"); });
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.classList.remove("is-visible"); }, 2600);
  }

  /* -------------------------------------------------- drawer ------------ */
  var Drawer = {
    el: null, overlay: null, content: null,
    init: function () {
      this.el = $("#CartDrawer");
      this.overlay = $("#CartDrawerOverlay");
      this.content = $("#CartDrawerContent");
      var close = $("#CloseCartDrawer");
      var self = this;
      if (close) close.addEventListener("click", function () { self.close(); });
      if (this.overlay) this.overlay.addEventListener("click", function () { self.close(); });
      document.addEventListener("keydown", function (e) { if (e.key === "Escape") self.close(); });
    },
    open: function () {
      if (!this.el) return;
      this.el.classList.add("open");
      if (this.overlay) this.overlay.classList.add("active");
      document.body.style.overflow = "hidden";
      document.body.classList.add("fl-drawer-open");
    },
    close: function () {
      if (!this.el) return;
      this.el.classList.remove("open");
      if (this.overlay) this.overlay.classList.remove("active");
      document.body.style.overflow = "";
      document.body.classList.remove("fl-drawer-open");
    }
  };

  /* -------------------------------------------------- cart -------------- */
  var Cart = {
    add: function (idOrForm, qty) {
      var body, headers = { Accept: "application/json" };
      if (idOrForm instanceof HTMLFormElement) {
        body = new FormData(idOrForm);
      } else {
        body = JSON.stringify({ id: idOrForm, quantity: qty || 1 });
        headers["Content-Type"] = "application/json";
      }
      return fetch("/cart/add.js", { method: "POST", headers: headers, body: body })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw e; });
          return r.json();
        });
    },
    change: function (key, quantity) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) { return r.json(); });
    },
    get: function () { return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }); },

    // Refresh drawer markup from the server. The drawer is rendered in the
    // global layout, so the /cart response always contains #CartDrawerContent
    // (including app-injected content like Foxkit/Discount Ninja).
    refreshDrawer: function () {
      if (!Drawer.content) return Promise.resolve();
      return fetch("/cart?view=drawer", { headers: { "X-Requested-With": "fetch" } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var fresh = doc.querySelector("#CartDrawerContent");
          if (fresh && Drawer.content) Drawer.content.innerHTML = fresh.innerHTML;
          Cart.bindDrawer();
          updateShipBar();
        })
        .catch(function () { /* keep current drawer content on failure */ });
    },

    updateCount: function (count) {
      $all("#CartCount, [data-cart-count]").forEach(function (el) {
        el.textContent = count;
        el.classList.toggle("is-empty", count === 0);
      });
      Frontline.updateShipBar();
    },

    bindDrawer: function () {
      $all(".cart-drawer-item__remove-link").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          var key = this.dataset.key, row = this.closest(".cart-drawer-item");
          if (row) row.style.opacity = ".4";
          Cart.change(key, 0).then(function (cart) {
            Cart.updateCount(cart.item_count);
            Cart.refreshDrawer();
          });
        });
      });
      $all(".quantity-wrapper-drawer").forEach(function (wrap) {
        var key = wrap.dataset.key;
        var minus = $(".cart-drawer__quantity-btn-minus", wrap);
        var plus = $(".cart-drawer__quantity-btn-plus", wrap);
        var val = $(".cart-drawer__quantity-value", wrap);
        function set(q) {
          if (q < 1) return;
          Cart.change(key, q).then(function (cart) {
            if (val) val.textContent = q;
            Cart.updateCount(cart.item_count);
            Cart.refreshDrawer();
          });
        }
        if (minus) minus.addEventListener("click", function () { set(parseInt(val.textContent, 10) - 1); });
        if (plus) plus.addEventListener("click", function () { set(parseInt(val.textContent, 10) + 1); });
      });
      Cart.loadUpsell();
    },

    // In-drawer cross-sell: recommend products related to the cart, with
    // one-tap quick-add. Hidden until at least one valid suggestion loads.
    loadUpsell: function () {
      var wrap = $("[data-cart-upsell]");
      var row = $("[data-cart-upsell-row]");
      if (!wrap || !row) return;
      Cart.get().then(function (cart) {
        if (!cart.items || !cart.items.length) { wrap.hidden = true; return; }
        var seed = cart.items[cart.items.length - 1].product_id;
        var inCart = cart.items.map(function (i) { return i.product_id; });
        fetch("/recommendations/products.json?product_id=" + seed + "&limit=8&intent=related", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var recs = (data.products || []).filter(function (p) { return inCart.indexOf(p.id) === -1; }).slice(0, 4);
            if (!recs.length) { wrap.hidden = true; return; }
            function cents(x) { return typeof x === "string" ? Math.round(parseFloat(x.replace(",", ".")) * 100) : x; }
            row.innerHTML = recs.map(function (p) {
              var avail = (p.variants || []).filter(function (x) { return x.available; });
              var v = avail[0] || (p.variants || [])[0];
              if (!v) return "";
              var img = (p.featured_image) || (p.images && p.images[0]) || "";
              var price = cents(v.price);
              var cmp = v.compare_at_price ? cents(v.compare_at_price) : 0;
              var priceHtml = (cmp > price ? '<s>' + formatMoney(cmp) + '</s> ' : '') + '<span class="is-now">' + formatMoney(price) + '</span>';
              var plus = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 5v14M5 12h14"/></svg>';
              var multi = avail.length > 1;
              // Compact single row: thumb · name+price · (size select) · add button.
              var sel = "";
              if (multi) {
                var opts = avail.map(function (vv) { var sz = (vv.title || '').split(' / ').pop(); return '<option value="' + vv.id + '">' + sz + '</option>'; }).join("");
                sel = '<select class="fl-cart-upsell__size" data-upsell-select aria-label="Kies maat">' + opts + '</select>';
              }
              var addBtn = '<button type="button" class="fl-cart-upsell__add" data-fl-add="' + (multi ? avail[0].id : v.id) + '"' + (multi ? ' data-upsell-add' : '') + ' aria-label="Toevoegen aan winkelwagen">' + plus + '</button>';
              return '<div class="fl-cart-upsell__item">' +
                (img ? '<a href="' + p.url + '" class="fl-cart-upsell__img"><img src="' + img + '&width=120" alt="" loading="lazy"></a>' : '') +
                '<div class="fl-cart-upsell__meta">' +
                  '<a href="' + p.url + '" class="fl-cart-upsell__name">' + p.title + '</a>' +
                  '<span class="fl-cart-upsell__price">' + priceHtml + '</span>' +
                '</div>' + sel + addBtn +
              '</div>';
            }).join("");
            // Size select updates its add button's variant id.
            row.querySelectorAll("[data-upsell-select]").forEach(function (sel) {
              sel.addEventListener("change", function () {
                var btn = sel.parentNode.querySelector("[data-upsell-add]");
                if (btn) btn.setAttribute("data-fl-add", sel.value);
              });
            });
            wrap.hidden = false;
          })
          .catch(function () { wrap.hidden = true; });
      });
    }
  };

  /* -------------------------------------------------- add-to-cart forms - */
  function bindProductForms() {
    $all('form[action^="/cart/add"], #AddToCartForm').forEach(function (form) {
      if (form._flBound) return; form._flBound = true;
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        if (btn) { btn.classList.add("is-loading"); btn.disabled = true; }
        Cart.add(form)
          .then(function () { return Cart.get(); })
          .then(function (cart) {
            Cart.updateCount(cart.item_count);
            document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
            if (CFG.cartType === "page") { window.location.href = "/cart"; return; }
            if (CFG.cartDrawer === false) { toast(t("added", "Toegevoegd aan winkelwagen")); return; }
            return Cart.refreshDrawer().then(function () {
              Drawer.open();
              toast(t("added", "Toegevoegd aan winkelwagen"));
            });
          })
          .catch(function (err) {
            toast((err && err.description) || t("error", "Er ging iets mis"));
          })
          .finally(function () { if (btn) { btn.classList.remove("is-loading"); btn.disabled = false; } });
      });
    });
  }

  // Quick-add buttons on product cards (data-fl-add="<variantId>").
  function bindQuickAdd() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-fl-add]");
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute("data-fl-add");
      if (!id) return;
      btn.classList.add("is-loading");
      Cart.add(id, 1)
        .then(function () { return Cart.get(); })
        .then(function (cart) {
          Cart.updateCount(cart.item_count);
          document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          if (CFG.cartDrawer === false) { toast(t("added", "Toegevoegd aan winkelwagen")); return; }
          return Cart.refreshDrawer().then(function () { Drawer.open(); toast(t("added", "Toegevoegd aan winkelwagen")); });
        })
        .catch(function (err) { toast((err && err.description) || t("error", "Er ging iets mis")); })
        .finally(function () { btn.classList.remove("is-loading"); });
    });
  }

  // Open drawer instead of navigating to /cart (drawer mode).
  function bindCartLinks() {
    if (CFG.cartType !== "drawer" || CFG.cartDrawer === false) return;
    document.addEventListener("click", function (e) {
      var link = e.target.closest('a[href$="/cart"], a[href="/cart"], [data-cart-toggle]');
      if (!link) return;
      e.preventDefault();
      Drawer.open();           // open instantly — no waiting on the network
      Cart.refreshDrawer();    // refresh contents in the background
    });
  }

  /* -------------------------------------------------- free-ship bar ----- */
  function updateShipBar() {
    var bars = $all("[data-ship-bar]");
    if (!bars.length || !CFG.freeShipThreshold) return;
    Cart.get().then(function (cart) {
      var total = cart.total_price, threshold = CFG.freeShipThreshold;
      var pct = Math.min(100, Math.round((total / threshold) * 100));
      var remaining = Math.max(0, threshold - total);
      bars.forEach(function (bar) {
        var fill = $(".fl-ship__fill", bar), msg = $(".fl-ship__msg", bar), node = $(".fl-ship__node", bar);
        if (fill) fill.style.width = pct + "%";
        if (node) node.style.left = pct + "%";
        if (msg) {
          if (remaining <= 0) { msg.innerHTML = "Gefeliciteerd! Je hebt <b>gratis verzending</b> ontgrendeld!"; bar.classList.add("is-complete"); }
          else { msg.innerHTML = "Nog <b>" + formatMoney(remaining) + "</b> tot <b>gratis verzending</b>"; bar.classList.remove("is-complete"); }
        }
      });
    });
  }

  /* -------------------------------------------------- modal ------------- */
  function bindModals() {
    document.addEventListener("click", function (e) {
      var open = e.target.closest("[data-fl-modal-open]");
      if (open) { e.preventDefault(); var m = $("#" + open.getAttribute("data-fl-modal-open")); if (m) { m.classList.add("is-open"); document.body.style.overflow = "hidden"; } }
      var close = e.target.closest("[data-fl-modal-close], .fl-modal__overlay");
      if (close) { var md = close.closest(".fl-modal"); if (md) { md.classList.remove("is-open"); document.body.style.overflow = ""; } }
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") $all(".fl-modal.is-open").forEach(function (m) { m.classList.remove("is-open"); document.body.style.overflow = ""; });
    });
  }

  /* -------------------------------------------------- sticky buy bar ---- */
  function bindStickyBar() {
    var bar = $("[data-sticky-atc]");
    var anchor = $("[data-sticky-atc-anchor]");
    if (!bar || !anchor || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      var show = !entries[0].isIntersecting;
      bar.classList.toggle("is-visible", show);
      // raise the sales-pop toast so it never overlaps the sticky bar
      document.body.classList.toggle("fl-sticky-atc-on", show);
    }, { rootMargin: "0px 0px -40% 0px" });
    io.observe(anchor);
  }

  /* -------------------------------------------------- header on scroll -- */
  function bindHeaderScroll() {
    var header = $("[data-header]");
    if (!header) return;
    var last = 0;
    window.addEventListener("scroll", function () {
      var y = window.pageYOffset;
      header.classList.toggle("is-stuck", y > 8);
      last = y;
    }, { passive: true });
  }

  /* -------------------------------------------------- wishlist ---------- */
  // Fully custom, localStorage-backed wishlist (replaces the Swym app).
  var Wish = {
    key: "fl_wishlist",
    get: function () { try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; } },
    save: function (list) { localStorage.setItem(this.key, JSON.stringify(list)); this.syncUI(); },
    has: function (h) { return this.get().indexOf(h) !== -1; },
    toggle: function (h) { var l = this.get(), i = l.indexOf(h); if (i === -1) l.push(h); else l.splice(i, 1); this.save(l); return i === -1; },
    syncUI: function () {
      var list = this.get();
      $all("[data-fl-wish]").forEach(function (b) { b.classList.toggle("is-active", list.indexOf(b.getAttribute("data-handle")) !== -1); });
      $all("[data-wishlist-count]").forEach(function (el) { el.textContent = list.length; el.classList.toggle("is-empty", list.length === 0); });
    },
    renderPage: function () {
      var grid = $("[data-wishlist-grid]"); if (!grid) return;
      var list = this.get(), empty = $("[data-wishlist-empty]");
      grid.innerHTML = "";
      if (!list.length) { if (empty) empty.style.display = ""; this.syncUI(); return; }
      if (empty) empty.style.display = "none";
      list.forEach(function (h) {
        fetch("/products/" + h + "?view=card")
          .then(function (r) { return r.ok ? r.text() : ""; })
          .then(function (html) {
            if (!html.trim()) return;
            var tmp = document.createElement("div"); tmp.innerHTML = html.trim();
            var card = tmp.querySelector("[data-product-card]") || tmp.firstElementChild;
            if (card) { grid.appendChild(card); Wish.syncUI(); }
          });
      });
    }
  };
  function bindWish() {
    document.addEventListener("click", function (e) {
      var b = e.target.closest("[data-fl-wish]"); if (!b) return;
      e.preventDefault();
      var h = b.getAttribute("data-handle"); if (!h) return;
      var added = Wish.toggle(h);
      toast(added ? "Toegevoegd aan verlanglijst" : "Verwijderd uit verlanglijst");
      if (!added) {
        var grid = b.closest("[data-wishlist-grid]");
        if (grid) { var card = b.closest("[data-product-card]"); if (card) card.remove(); var empty = $("[data-wishlist-empty]"); if (empty && Wish.get().length === 0) empty.style.display = ""; }
      }
    });
  }

  /* -------------------------------------------------- lazy helper ------ */
  // Run cb once when el nears the viewport — keeps per-page request counts low
  // (avoids firing many /products?view=card requests on initial load).
  function lazyOnce(el, cb) {
    if (!el) return;
    if (!("IntersectionObserver" in window)) { cb(); return; }
    var io = new IntersectionObserver(function (ents) {
      if (ents[0].isIntersecting) { io.disconnect(); cb(); }
    }, { rootMargin: "700px 0px" });
    io.observe(el);
  }

  /* -------------------------------------------------- recently viewed -- */
  // Recently viewed — stores a small product object per visit and renders the
  // rail straight from localStorage. No per-product fetches (keeps request
  // volume low so Cloudflare doesn't challenge the page).
  var Recent = {
    key: "fl_recently_viewed_v2",
    get: function () { try { return (JSON.parse(localStorage.getItem(this.key)) || []).filter(function (x) { return x && x.h && x.u; }); } catch (e) { return []; } },
    track: function () {
      var p = window.FL_PRODUCT; if (!p || !p.h) return;
      var list = this.get().filter(function (x) { return x.h !== p.h; });
      list.unshift({ h: p.h, t: p.t, p: p.p, i: p.i, u: p.u });
      try { localStorage.setItem(this.key, JSON.stringify(list.slice(0, 12))); } catch (e) {}
    },
    render: function () {
      var sec = $("[data-recently-viewed]"); if (!sec) return;
      var grid = $("[data-recently-viewed-grid]", sec);
      var limit = Math.min(parseInt(sec.getAttribute("data-limit"), 10) || 8, 10);
      var current = window.FL_PRODUCT && window.FL_PRODUCT.h;
      var list = this.get().filter(function (o) { return o.h !== current; }).slice(0, limit);
      if (!list.length) return;
      grid.innerHTML = list.map(function (o) {
        var price = (window.Frontline && o.p != null) ? Frontline.formatMoney(o.p) : "";
        return '<a class="fl-rv-card" href="' + o.u + '">' +
          '<span class="fl-rv-card__media">' + (o.i ? '<img src="' + o.i + '" alt="" loading="lazy">' : "") + '</span>' +
          '<span class="fl-rv-card__title">' + (o.t || "") + '</span>' +
          (price ? '<span class="fl-rv-card__price">' + price + '</span>' : "") +
          '</a>';
      }).join("");
      sec.hidden = false;
    }
  };

  /* -------------------------------------------------- predictive search */
  function bindSearch() {
    var wrap = $("#fl-search"); if (!wrap) return;
    var input = $("[data-search-input]", wrap);
    var results = $("[data-search-results]", wrap);
    function open() { wrap.classList.add("is-open"); document.body.style.overflow = "hidden"; setTimeout(function () { if (input) input.focus(); }, 60); }
    function close() { wrap.classList.remove("is-open"); document.body.style.overflow = ""; }
    $all("[data-search-open]").forEach(function (b) { b.addEventListener("click", open); });
    wrap.addEventListener("click", function (e) { if (e.target.closest("[data-search-close]")) close(); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });
    if (!input) return;
    var t;
    input.addEventListener("input", function () {
      var q = input.value.trim();
      clearTimeout(t);
      if (q.length < 2) { results.innerHTML = ""; return; }
      t = setTimeout(function () {
        fetch("/search/suggest.json?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last")
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (!products.length) { results.innerHTML = '<p class="fl-search__empty">Geen resultaten voor "' + q + '"</p>'; return; }
            results.innerHTML = products.map(function (p) {
              var img = (p.featured_image && p.featured_image.url) ? p.featured_image.url : (p.image || "");
              var price = p.price ? '<span class="fl-search__price">' + p.price + "</span>" : "";
              return '<a class="fl-search__item" href="' + p.url + '">' +
                (img ? '<img src="' + img + '" alt="" loading="lazy">' : '<span class="fl-search__noimg"></span>') +
                '<span class="fl-search__meta"><span class="fl-search__title">' + p.title + "</span>" + price + "</span></a>";
            }).join("") + '<a class="fl-search__all" href="/search?q=' + encodeURIComponent(q) + '">Alle resultaten voor &ldquo;' + q + '&rdquo; &rarr;</a>';
          })
          .catch(function () { /* ignore */ });
      }, 220);
    });
  }

  /* -------------------------------------------------- card quick toggle - */
  function bindQuickToggle() {
    document.addEventListener("click", function (e) {
      var t = e.target.closest("[data-quick-toggle]");
      if (!t) {
        if (!e.target.closest(".fl-card__quick")) $all(".fl-card__quick.is-open").forEach(function (q) { q.classList.remove("is-open"); });
        return;
      }
      e.preventDefault();
      var wrap = t.closest(".fl-card__media-wrap");
      var panel = wrap ? wrap.querySelector(".fl-card__quick") : null;
      $all(".fl-card__quick.is-open").forEach(function (q) { if (q !== panel) q.classList.remove("is-open"); });
      if (panel) panel.classList.toggle("is-open");
    });
  }

  /* -------------------------------------------------- public API -------- */
  var Frontline = window.Frontline = {
    toast: toast, formatMoney: formatMoney, Cart: Cart, Drawer: Drawer,
    updateShipBar: updateShipBar, Wish: Wish, Recent: Recent,
    bindCardImages: function () { bindCardImages(); }
  };

  // Cart reservation timer — persistent 10-min countdown (urgency). One global
  // interval that updates whichever [data-reserve-time] exists after re-renders.
  function bindReserve() {
    var KEY = "fl_reserve_until";
    var MINS = (typeof CFG.reserveMinutes === "number" && CFG.reserveMinutes > 0) ? CFG.reserveMinutes : 10;
    function deadline() {
      var v = parseInt(sessionStorage.getItem(KEY), 10);
      if (!v || v < Date.now()) { v = Date.now() + MINS * 60 * 1000; sessionStorage.setItem(KEY, v); }
      return v;
    }
    function tick() {
      var el = document.querySelector("[data-reserve-time]"); if (!el) return;
      var diff = Math.max(0, deadline() - Date.now());
      var m = Math.floor(diff / 60000), s = Math.floor((diff % 60000) / 1000);
      el.textContent = (m < 10 ? "0" : "") + m + ":" + (s < 10 ? "0" : "") + s;
      if (diff <= 0) sessionStorage.removeItem(KEY);
    }
    setInterval(tick, 1000); tick();
  }

  /* -------------------------------------------------- scroll reveal ----- */
  function countUp(scope) {
    var nums = (scope.matches && scope.matches("[data-count-to]")) ? [scope] : $all("[data-count-to]", scope);
    nums.forEach(function (el) {
      if (el._counted) return; el._counted = 1;
      var to = parseFloat(el.getAttribute("data-count-to")) || 0;
      var dec = parseInt(el.getAttribute("data-decimals"), 10) || 0;
      var pre = el.getAttribute("data-prefix") || "", suf = el.getAttribute("data-suffix") || "";
      var dur = 1500, start = performance.now();
      function fmt(v) { return pre + (dec ? v.toFixed(dec).replace(".", ",") : Math.round(v).toLocaleString("nl-NL")) + suf; }
      function step(now) { var p = Math.min(1, (now - start) / dur); var e = 1 - Math.pow(1 - p, 3); el.textContent = fmt(to * e); if (p < 1) requestAnimationFrame(step); else el.textContent = fmt(to); }
      requestAnimationFrame(step);
    });
  }
  function bindReveal() {
    var els = $all("[data-reveal]");
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) { els.forEach(function (e) { e.classList.add("is-revealed"); countUp(e); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          var el = en.target;
          var d = parseInt(el.getAttribute("data-reveal-delay"), 10) || 0;
          setTimeout(function () { el.classList.add("is-revealed"); countUp(el); }, d);
          io.unobserve(el);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.12 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ------------------------------------------------ PDP colour block ---- */
  // The colour swatches are rendered by an app and link to sibling colour
  // products. Show the "Kleur" label + block only when the app actually drew
  // visible swatches; hide it for single-colour products (no real choice).
  function enhancePDPColour() {
    var form = document.getElementById("AddToCartForm"); if (!form) return;
    var lab = form.querySelector(".fl-pdp__label--kleur"); if (!lab) return;
    var block = lab.closest(".fl-pdp__appblock") || lab.parentNode; if (!block) return;
    block.style.display = ""; // make measurable before checking
    var cands = block.querySelectorAll('a, button, input, select, img, [class*="swatch"], [class*="Swatch"], [class*="option"]');
    var visible = false;
    for (var i = 0; i < cands.length; i++) {
      var el = cands[i];
      if (lab.contains(el) || el === lab) continue;
      if (el.offsetWidth > 0 && el.offsetHeight > 0) { visible = true; break; }
    }
    lab.hidden = !visible;
    block.style.display = visible ? "" : "none";
  }
  function bindPDPColour() {
    var form = document.getElementById("AddToCartForm"); if (!form) return;
    enhancePDPColour();
    if ("MutationObserver" in window) {
      var mo = new MutationObserver(function () { enhancePDPColour(); });
      mo.observe(form, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 6000);
    }
  }

  /* ---------------------------------------- product-card image peek ----- */
  // On touch devices the hover-swap to the 2nd image is unavailable, so we let
  // shoppers swipe (or tap the dots) to peek the alternate image. A horizontal
  // swipe switches the image and suppresses the card link so it doesn't open.
  function bindCardImages() {
    var TOUCH = window.matchMedia && window.matchMedia("(hover: none)").matches;
    $all(".fl-card__media-wrap").forEach(function (wrap) {
      if (wrap._flImg) return; wrap._flImg = true;
      var hasAlt = wrap.querySelector(".fl-card__img--alt");
      if (!hasAlt) return;
      function setAlt(on) { wrap.classList.toggle("is-alt", !!on); syncDots(); }
      var dots = wrap.querySelector("[data-card-dots]");
      function syncDots() {
        if (!dots) return;
        var alt = wrap.classList.contains("is-alt");
        dots.querySelectorAll("[data-card-dot]").forEach(function (d) {
          d.classList.toggle("is-active", d.getAttribute("data-card-dot") === (alt ? "1" : "0"));
        });
      }
      if (dots) {
        dots.addEventListener("click", function (e) {
          var d = e.target.closest("[data-card-dot]"); if (!d) return;
          e.preventDefault(); e.stopPropagation();
          setAlt(d.getAttribute("data-card-dot") === "1");
        });
      }
      if (!TOUCH) return; // desktop already swaps on hover via CSS
      var x0 = 0, y0 = 0, moved = false, swiped = false;
      var link = wrap.querySelector("a.fl-card__media");
      wrap.addEventListener("touchstart", function (e) {
        var t = e.changedTouches[0]; x0 = t.clientX; y0 = t.clientY; moved = false;
      }, { passive: true });
      wrap.addEventListener("touchmove", function (e) {
        var t = e.changedTouches[0];
        var dx = Math.abs(t.clientX - x0), dy = Math.abs(t.clientY - y0);
        // Only a deliberate, clearly-horizontal drag counts as a swipe — a
        // small finger drift during a tap must still open the product.
        if (dx > 30 && dx > dy * 1.5) moved = true;
      }, { passive: true });
      wrap.addEventListener("touchend", function (e) {
        if (!moved) return;
        var dx = e.changedTouches[0].clientX - x0;
        setAlt(dx < 0); // swipe left → alt, swipe right → main
        // Suppress only the ghost click this swipe fires, not later taps.
        swiped = true;
        setTimeout(function () { swiped = false; }, 400);
      }, { passive: true });
      // Cancel the card-link tap when the gesture was a swipe.
      if (link) link.addEventListener("click", function (e) { if (swiped) { e.preventDefault(); swiped = false; } });
    });
  }

  /* ---------------------------------------- policy pages ---------------- */
  // Shopify rendert shop-policies (/policies/*) met zijn eigen default markup
  // (.shopify-policy__*) en negeert templates/policy.liquid. We vormen die
  // markup hier om naar de fl-page-structuur, zodat dezelfde premium styling
  // (hero-band, sticky inhoudsopgave, support-CTA) als op de andere pagina's
  // automatisch van toepassing is.
  function buildPageTOC(root) {
    root = root || document.querySelector("[data-fl-page]");
    if (!root) return;
    var body = root.querySelector("[data-page-body]"),
        toc = root.querySelector("[data-page-toc]"),
        nav = root.querySelector("[data-page-toc-nav]");
    if (!body || !toc || !nav) return;
    var heads = [].slice.call(body.querySelectorAll("h2"));
    if (heads.length < 3) return;
    heads.forEach(function (h, i) {
      if (!h.id) h.id = "sec-" + i + "-" + (h.textContent || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
      var a = document.createElement("a");
      a.href = "#" + h.id; a.textContent = h.textContent; a.className = "fl-page__toc-link";
      a.addEventListener("click", function (e) { e.preventDefault(); window.scrollTo({ top: h.getBoundingClientRect().top + window.pageYOffset - 100, behavior: "smooth" }); history.replaceState(null, "", "#" + h.id); });
      nav.appendChild(a);
    });
    root.classList.add("has-toc"); toc.hidden = false;
    // Desktop: het sticky zijbalk-menu staat open; mobiel: ingeklapt.
    if (!window.matchMedia || window.matchMedia("(min-width: 850px)").matches) toc.open = true;
    if ("IntersectionObserver" in window) {
      var links = [].slice.call(nav.children);
      var io = new IntersectionObserver(function (entries) { entries.forEach(function (en) { if (en.isIntersecting) { links.forEach(function (l) { l.classList.toggle("is-active", l.getAttribute("href") === "#" + en.target.id); }); } }); }, { rootMargin: "-90px 0px -70% 0px" });
      heads.forEach(function (h) { io.observe(h); });
    }
  }

  // Sommige policy-teksten gebruiken "<p><strong>KOP</strong><br>tekst…</p>" in
  // plaats van echte <h2>-koppen. We zetten die om naar echte koppen zodat ze
  // én netjer ogen én in de inhoudsopgave verschijnen.
  function normalizePolicyHeadings(body) {
    [].slice.call(body.querySelectorAll("p")).forEach(function (p) {
      var first = p.firstChild;
      while (first && first.nodeType === 3 && !first.textContent.trim()) first = first.nextSibling;
      if (!first || first.nodeType !== 1 || first.tagName !== "STRONG") return;
      var headText = (first.textContent || "").replace(/\s+/g, " ").trim();
      if (headText.length < 2 || headText.length > 90) return;
      var after = first.nextSibling;
      while (after && after.nodeType === 3 && !after.textContent.trim()) after = after.nextSibling;
      var brAfter = after && after.nodeName === "BR";
      var brInside = !!first.querySelector("br");
      if (!brAfter && !brInside) return; // gewone inline-bold, geen sectiekop
      var h2 = document.createElement("h2");
      h2.textContent = headText;
      p.parentNode.insertBefore(h2, p);
      p.removeChild(first);
      if (brAfter && after && after.parentNode === p) p.removeChild(after);
      var n = p.firstChild;
      while (n && ((n.nodeType === 3 && !n.textContent.trim()) || n.nodeName === "BR")) { var rm = n; n = n.nextSibling; p.removeChild(rm); }
      if (!p.textContent.trim() && !p.querySelector("img,a,strong,span")) p.parentNode.removeChild(p);
    });
  }

  function enhancePolicyPage() {
    var src = document.querySelector(".shopify-policy__container");
    if (!src || document.querySelector(".fl-page--policy")) return;
    var titleNode = src.querySelector(".shopify-policy__title");
    var bodyNode = src.querySelector(".shopify-policy__body .rte") || src.querySelector(".shopify-policy__body");
    var title = titleNode ? (titleNode.textContent || "").trim() : (document.title || "Beleid");
    var bodyHTML = bodyNode ? bodyNode.innerHTML : "";

    var sec = document.createElement("section");
    sec.className = "fl-page fl-page--policy";
    sec.innerHTML =
      '<div class="fl-page__hero"><div class="fl-container">' +
        '<p class="fl-eyebrow">Frontline</p>' +
        '<h1 class="fl-page__title"></h1>' +
      "</div></div>" +
      '<div class="fl-container fl-page__main">' +
        '<div class="fl-page__layout" data-fl-page>' +
          '<details class="fl-page__toc" data-page-toc hidden>' +
            '<summary class="fl-page__toc-h">Op deze pagina</summary>' +
            '<nav class="fl-page__toc-nav" data-page-toc-nav></nav>' +
          "</details>" +
          '<div class="fl-page__body rte" data-page-body></div>' +
        "</div>" +
        '<div class="fl-page__cta">' +
          '<div class="fl-page__cta-txt"><h3>Nog vragen?</h3><p>Ons klantenserviceteam helpt je graag verder.</p></div>' +
          '<a class="fl-btn fl-btn--lg" href="/pages/contact">Neem contact op</a>' +
        "</div>" +
      "</div>";
    sec.querySelector(".fl-page__title").textContent = title;
    var bodyDiv = sec.querySelector("[data-page-body]");
    bodyDiv.innerHTML = bodyHTML;
    try { normalizePolicyHeadings(bodyDiv); } catch (e) {}
    src.parentNode.replaceChild(sec, src);
    buildPageTOC(sec);
  }

  function init() {
    Drawer.init();
    bindReserve();
    bindReveal();
    Cart.bindDrawer();
    bindProductForms();
    bindQuickAdd();
    bindQuickToggle();
    bindCartLinks();
    bindModals();
    bindStickyBar();
    bindHeaderScroll();
    bindWish();
    bindCardImages();
    bindPDPColour();
    enhancePolicyPage();
    Wish.syncUI();
    Wish.renderPage();
    Recent.track();
    Recent.render();
    bindSearch();
    updateShipBar();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
